import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { createColumnHelper } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import RoutedSPADialog from '@/components/admin/SysadminSPA/shared/RoutedSPADialog';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPADataTable from '@/components/admin/SysadminSPA/shared/SPADataTable';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';
import ProgramLicenseStatesDialog from './ProgramLicenseStatesDialog';
import type { ProgramLicenseRow } from './ProgramLicenseStatesDialog';

interface LicenseRowsProps {
  isLoading: boolean;
  labelOverrides: Record<number, string>;
  onEditStates: (row: ProgramLicenseRow) => void;
  rows: any[];
}

interface LicenseTableMeta {
  labelOverrides: Record<number, string>;
  onEditStates: (row: ProgramLicenseRow) => void;
}

const columnHelper = createColumnHelper<any>();

// AIDEV-NOTE: Sorting is off and defaultSort is cleared — rows are ordered by the license's
// `position`, matching the legacy DataTable's hidden sort column. SPADataTable otherwise defaults
// to sorting on a `title` column that does not exist here.
const licenseColumns = [
  columnHelper.accessor('name', {
    enableSorting: false,
    header: 'License',
  }),
  columnHelper.display({
    cell: ({ row, table }) => {
      const { labelOverrides, onEditStates } = table.options.meta as LicenseTableMeta;
      const license = row.original;

      return (
        <SPAButton
          size="xs"
          onClick={() => onEditStates({ id: license.id, name: license.name })}
        >
          {/* No globe/world SVG in ui/Icon, so this keeps the legacy FA glyph. */}
          <i aria-hidden="true" className="fa fa-globe" />
          {labelOverrides[license.id] ?? license.default_state_message}
        </SPAButton>
      );
    },
    header: 'Availability By Default',
    id: 'availability',
  }),
];

// AIDEV-NOTE: SPA-scoped replacement for the shared useDefaultProgramLicensesData hook — one
// request also carries the program title for the header subtitle. The whole modal mounts per
// route match, so this refetches per open instead of caching the first program it saw.
const useProgramLicenses = (programId: number) => {
  const [isLoading, setIsLoading] = useState(true);
  const [licenseRows, setLicenseRows] = useState<any[]>([]);
  const [programTitle, setProgramTitle] = useState('');

  useEffect(() => {
    Axios.get(Routes.plato_api_program_path(programId), {
      params: {
        extra_attributes: ['program_licenses', 'licenses'],
        only: ['full_title_with_edition', 'licenses', 'program_licenses'],
      },
    }).then(({ data: { program } }) => {
      setProgramTitle(program.full_title_with_edition ?? '');

      const licenses = program.licenses ?? [];
      setLicenseRows((program.program_licenses ?? []).map((programLicense: any) => ({
        ...licenses.find((license: any) => license.id === programLicense.license_id),
        ...programLicense,
      })));
      setIsLoading(false);
    });
  }, [programId]);

  return { isLoading, licenseRows, programTitle };
};

const LicenseRows = ({ isLoading, labelOverrides, onEditStates, rows }: LicenseRowsProps) => {
  // The legacy DataTable sorted on a hidden `position` column; the API returns rows unordered.
  const sortedRows = [...rows].sort((a: any, b: any) => a.position - b.position);

  return (
    <SPADataTable
      columns={licenseColumns}
      data={sortedRows}
      defaultSort={[]}
      emptyMessage="No licenses for this program."
      isLoading={isLoading}
      loadingRows={2}
      meta={{ labelOverrides, onEditStates }}
    />
  );
};

// AIDEV-NOTE: SPA-native rebuild of the license availability modal. The legacy version rendered a
// jQuery DataTable whose cells hosted a jQuery States picker; both are replaced here so the whole
// flow themes with the SPA tokens and needs no CSS overrides against DataTables' own stylesheet.
// The legacy component is still used by /admin/programs and is untouched.
// Mounted as a child route (/content/programs/:programId/licenses) via RoutedSPADialog.
//
// The legacy footer's "Save" button was a no-op — Footer received no primaryButtonCallback, so it
// fell back to a default no-op, and it was type=submit with no enclosing form. Saving happens in
// the states dialog, so this modal is honestly read-only.
const SPALicenseAvailabilityModal = () => {
  const { programId } = useParams();
  const { isLoading, licenseRows, programTitle } = useProgramLicenses(Number(programId));
  const [editing, setEditing] = useState<ProgramLicenseRow | null>(null);
  const [labelOverrides, setLabelOverrides] = useState<Record<number, string>>({});

  return (
    <>
      <RoutedSPADialog>
        <SPADialog.Content size="md">
          <SPADialog.Header>
            <SPADialog.Title>Availability By Default Per License</SPADialog.Title>
            {/* min-h holds the subtitle line while the title loads, so the header doesn't jump. */}
            <SPADialog.Description className="tw-mt-1 tw-min-h-5 tw-text-spa-secondary">
              {programTitle}
            </SPADialog.Description>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body>
            <p className="tw-m-0 tw-mb-4 tw-text-sm tw-leading-5 tw-text-spa-primary">
              To modify which licenses are associated with the program, edit the program.
            </p>
            <LicenseRows
              isLoading={isLoading}
              labelOverrides={labelOverrides}
              rows={licenseRows}
              onEditStates={setEditing}
            />
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Close</SPAButton>
            </SPADialog.Close>
          </SPADialog.Footer>
        </SPADialog.Content>
      </RoutedSPADialog>

      <ProgramLicenseStatesDialog
        programLicense={editing}
        onClose={() => setEditing(null)}
        onSaved={(id, label) => setLabelOverrides(current => ({ ...current, [id]: label }))}
      />
    </>
  );
};

export default SPALicenseAvailabilityModal;
